import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "input",
    "results",
    "loading",
    "error",
    "manualToggle",
    "geocodingData",
    "setAddressField",
    "setAddressContainer",
    "addressLabel",
    "manualAddressField",
    "manualEntryField",
    "autocompleteContainer",
  ];
  static values = {
    url: { type: String, default: "/api/v1/locations/autocomplete" },
    locationId: Number,
    manualMode: Boolean,
    minChars: { type: Number, default: 3 },
    debounceMs: { type: Number, default: 300 },
    test: { type: Boolean, default: false },
  };

  connect() {
    console.log("Address autocomplete controller connected");
    console.log("Targets:", {
      hasInput: this.hasInputTarget,
      hasResults: this.hasResultsTarget,
      hasLoading: this.hasLoadingTarget,
    });

    // Only proceed if we have the required input target
    if (!this.hasInputTarget) {
      console.log(
        "Address autocomplete controller: input target not found, skipping initialization",
      );
      return;
    }

    this.searchTimeout = null;
    this.currentFocus = -1;
    this.cache = new Map();
    this.justSelectedUntil = 0;
    this.lastSelectedValue = null;

    // Initialize fields properly on edit
    this.initializeFields();

    // Set initial state
    if (this.hasManualToggleTarget) {
      this.updateManualToggle();
    }

    // Show/hide set address field based on initial mode
    this.updateFieldVisibility();

    // Ensure hidden manual flag stays in sync
    if (this.hasManualEntryFieldTarget) {
      this.manualEntryFieldTarget.value = this.manualModeValue
        ? "true"
        : "false";
    }

    // Disable browser autocomplete more aggressively
    this.disableBrowserAutocomplete();

    // Bind handlers once so they can be properly removed in disconnect
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleChange = () => this.hideResults();
    this.boundHandleBlur = () => setTimeout(() => this.hideResults(), 300);
    this.boundHandleResultsMousedown = this.handleResultsMousedown.bind(this);

    // Add keyboard event listeners
    this.inputTarget.addEventListener("keydown", this.boundHandleKeydown);
    this.inputTarget.addEventListener("change", this.boundHandleChange);
    this.inputTarget.addEventListener("blur", this.boundHandleBlur);

    // Single delegated listener for results container (avoids accumulation)
    if (this.hasResultsTarget) {
      this.resultsTarget.addEventListener("mousedown", this.boundHandleResultsMousedown);
    }
  }

  isTestEnv() {
    return (
      this.testValue || document.querySelector('meta[name="test-env"]') !== null
    );
  }

  initializeFields() {
    // The visible address field should always reflect the actual saved address (set_address)
    // The set_address hidden field should be kept in sync when changes are made
    if (this.hasSetAddressFieldTarget) {
      const addressValue = this.inputTarget.value;

      // Make sure set_address is always in sync with what's displayed
      if (addressValue && !this.manualModeValue) {
        this.setAddressFieldTarget.value = addressValue;
      }
    }

    // Initialize manual address field if in manual mode
    if (this.manualModeValue && this.hasManualAddressFieldTarget) {
      this.manualAddressFieldTarget.value = this.setAddressFieldTarget.value;
    }
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("keydown", this.boundHandleKeydown);
      this.inputTarget.removeEventListener("change", this.boundHandleChange);
      this.inputTarget.removeEventListener("blur", this.boundHandleBlur);
    }
    if (this.hasResultsTarget) {
      this.resultsTarget.removeEventListener("mousedown", this.boundHandleResultsMousedown);
    }
  }

  // Triggered on input
  search(event) {
    console.log("Search triggered with:", event.target.value);
    // If a suggestion was just selected, browsers may fire 2 change events:
    // - our synthetic change
    // - the native change on blur
    // Guard against re-opening the dropdown in that window
    if (
      event &&
      event.type === "change" &&
      this.justSelectedUntil &&
      Date.now() < this.justSelectedUntil
    ) {
      this.justSelectedUntil = 0;
      this.suppressNextSearch = false;
      return;
    }
    if (this.suppressNextSearch) {
      this.suppressNextSearch = false;
      return;
    }
    const query = event.target.value.trim();

    // If the current value matches the last selected address, do not re-query on change
    if (
      event &&
      event.type === "change" &&
      this.lastSelectedValue &&
      query === this.lastSelectedValue
    ) {
      this.hideResults();
      return;
    }

    if (this.manualModeValue) {
      // In manual mode, keep set_address in sync with the visible input
      if (this.hasSetAddressFieldTarget) {
        this.setAddressFieldTarget.value = event.target.value;
      }
      if (this.hasManualAddressFieldTarget) {
        this.manualAddressFieldTarget.value = event.target.value;
      }
      return;
    }

    // Clear geocoding data when user types (as they're changing the address)
    if (this.hasGeocodingDataTarget) {
      this.geocodingDataTarget.value = "";
    }

    // Clear timeout if exists
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Hide results if query too short
    if (query.length < this.minCharsValue) {
      this.hideResults();
      return;
    }

    // Check cache first
    const cached = this.cache.get(query);
    if (cached) {
      this.displayResults(cached);
      return;
    }

    // Show loading state
    this.showLoading();

    // In test environment, stub suggestions but still show loading briefly
    // Allow real fetch for explicit error-testing queries
    if (this.isTestEnv() && !/error/i.test(query)) {
      console.log("Using test stub data");
      const stub = [
        {
          id: "stub-1",
          place_name: `${query} - Result 1`,
          coordinates: [0, 0],
          context: [],
        },
        {
          id: "stub-2",
          place_name: `${query} - Result 2`,
          coordinates: [1, 1],
          context: [],
        },
      ];
      setTimeout(() => this.displayResults(stub), 150);
      return;
    }

    // Debounce the search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceMsValue);
  }

  async performSearch(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`;

      const csrf = document.querySelector('meta[name="csrf-token"]');
      const headers = { Accept: "application/json" };
      if (csrf && csrf.content) headers["X-CSRF-Token"] = csrf.content;

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Cache the results
      this.cache.set(query, data.suggestions);

      this.displayResults(data.suggestions);
    } catch (error) {
      console.error("Autocomplete error:", error);
      this.showError();
    }
  }

  displayResults(suggestions) {
    this.hideLoading();
    this.hideError();

    if (!suggestions || suggestions.length === 0) {
      this.hideResults();
      return;
    }

    // Store suggestions in controller for reference
    this.suggestions = suggestions;

    // Build results HTML
    const html = suggestions
      .map((suggestion, index) => {
        return `
        <div class="mapbox-autocomplete-item p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
             data-action="click->address-autocomplete#selectSuggestion"
             data-suggestion-index="${index}"
             data-index="${index}"
             role="option"
             aria-selected="false">
          <div class="text-sm font-medium text-neutral-900">${this.escapeHtml(suggestion.place_name)}</div>
        </div>
      `;
      })
      .join("");

    this.resultsTarget.innerHTML = html;
    this.resultsTarget.classList.remove("hidden");
    this.resultsTarget.style.display = "block";
    this.currentFocus = -1;
    // Event handling is delegated via the single mousedown listener on resultsTarget
    // set up in connect(), so no per-call listeners are needed here.
  }

  // Delegated mousedown handler for the results container (set up once in connect)
  handleResultsMousedown(e) {
    const item = e.target.closest(".mapbox-autocomplete-item");
    if (!item) return;

    const index = parseInt(item.dataset.index);
    const suggestion = this.suggestions[index];
    if (!suggestion) return;

    const addressValue = suggestion.place_name || suggestion.address;

    // Prevent default blur behavior
    e.preventDefault();

    this.inputTarget.value = addressValue;

    // Update hidden fields
    if (this.hasSetAddressFieldTarget) {
      this.setAddressFieldTarget.value = addressValue;
    }

    const geocodingField = this.findGeocodingField();
    if (geocodingField) {
      const geocodingData = {
        id: suggestion.id,
        place_name: suggestion.place_name,
        geometry: { coordinates: suggestion.coordinates },
        context: suggestion.context || [],
      };
      geocodingField.value = JSON.stringify(geocodingData);
    }

    this.hideResults();

    // Prevent the subsequent native change on blur from re-triggering search
    this.lastSelectedValue = addressValue;
    this.suppressNextSearch = true;
    this.justSelectedUntil = Date.now() + 800;

    // Fire a synthetic change to keep listeners consistent
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  findGeocodingField() {
    if (this.hasGeocodingDataTarget) return this.geocodingDataTarget;
    return (
      document.getElementById("location_geocoding_data_json") ||
      document.querySelector('input[name="location[geocoding_data_json]"]')
    );
  }

  selectSuggestion(event) {
    console.log("selectSuggestion called", event);

    // Get the index from the clicked element
    const index =
      event.currentTarget.dataset.suggestionIndex ||
      event.currentTarget.dataset.index;
    console.log("Suggestion index:", index);

    // Get the suggestion from our stored array
    const suggestion = this.suggestions[parseInt(index)];
    console.log("Selected suggestion:", suggestion);

    if (!suggestion) {
      console.error("No suggestion found at index:", index);
      return;
    }

    // Update input value
    const addressValue = suggestion.place_name || suggestion.address;
    console.log("Address value to set:", addressValue);
    this.inputTarget.value = addressValue;
    console.log("Input value after setting:", this.inputTarget.value);

    // IMPORTANT: Always populate the set_address field when selecting from Mapbox
    if (this.hasSetAddressFieldTarget) {
      this.setAddressFieldTarget.value = addressValue;
    }

    // Store geocoding data in hidden field for form submission
    // Try multiple ways to find the geocoding field
    let geocodingField = this.hasGeocodingDataTarget
      ? this.geocodingDataTarget
      : null;
    if (!geocodingField) {
      geocodingField = document.getElementById("location_geocoding_data_json");
    }
    if (!geocodingField) {
      geocodingField = document.querySelector(
        'input[name="location[geocoding_data_json]"]',
      );
    }

    if (geocodingField) {
      // Format the geocoding data properly for the backend
      const geocodingData = {
        id: suggestion.id,
        place_name: suggestion.place_name,
        geometry: { coordinates: suggestion.coordinates },
        context: suggestion.context || [],
      };
      geocodingField.value = JSON.stringify(geocodingData);
      console.log(
        "Set geocoding data via selectSuggestion:",
        geocodingField.value,
      );
    }

    // Store geocoding data if we have a location ID (for immediate save)
    if (this.locationIdValue) {
      this.saveGeocodingData(suggestion);
    }

    // Hide results
    this.hideResults();

    // Prevent the subsequent native change on blur from re-triggering search
    this.lastSelectedValue = addressValue;
    this.suppressNextSearch = true;
    this.justSelectedUntil = Date.now() + 800;

    // Trigger change event
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  async saveGeocodingData(suggestion) {
    try {
      const response = await fetch(
        `/api/v1/locations/${this.locationIdValue}/geocode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            mapbox_id: suggestion.id,
            place_name: suggestion.place_name,
            coordinates: suggestion.coordinates,
            context: suggestion.context || [],
          }),
        },
      );

      if (!response.ok) {
        console.error("Failed to save geocoding data");
      }
    } catch (error) {
      console.error("Error saving geocoding data:", error);
    }
  }

  toggleManualMode(event) {
    event.preventDefault();
    this.manualModeValue = !this.manualModeValue;

    if (this.manualModeValue) {
      this.hideResults();
      // Clear geocoding data when switching to manual mode
      if (this.hasGeocodingDataTarget) {
        this.geocodingDataTarget.value = "";
      }
      // Copy current address to manual field and set_address when switching to manual
      if (this.inputTarget.value) {
        if (this.hasSetAddressFieldTarget) {
          this.setAddressFieldTarget.value = this.inputTarget.value;
        }
        if (this.hasManualAddressFieldTarget) {
          this.manualAddressFieldTarget.value = this.inputTarget.value;
        }
      }
      // Persist in hidden field for form submission
      if (this.hasManualEntryFieldTarget) {
        this.manualEntryFieldTarget.value = "true";
      }
      this.markAsManualEntry();
    } else {
      // Clear set_address when switching back to Mapbox mode
      if (this.hasSetAddressFieldTarget) {
        this.setAddressFieldTarget.value = "";
      }
      // Persist in hidden field for form submission
      if (this.hasManualEntryFieldTarget) {
        this.manualEntryFieldTarget.value = "false";
      }
    }

    this.updateManualToggle();
    this.updateFieldVisibility();
  }

  async markAsManualEntry() {
    if (!this.locationIdValue) return;

    try {
      const response = await fetch(
        `/api/v1/locations/${this.locationIdValue}/manual_entry`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            address: this.inputTarget.value,
          }),
        },
      );

      if (!response.ok) {
        console.error("Failed to mark as manual entry");
      }
    } catch (error) {
      console.error("Error marking manual entry:", error);
    }
  }

  updateManualToggle() {
    if (!this.hasManualToggleTarget) return;

    if (this.manualModeValue) {
      this.manualToggleTarget.textContent = "Use Autocomplete";
      this.manualToggleTarget.classList.add("btn-brand-primary");
      this.manualToggleTarget.classList.remove("btn-brand-neutral");
    } else {
      this.manualToggleTarget.textContent = "Enter manually";
      this.manualToggleTarget.classList.remove("btn-brand-primary");
      this.manualToggleTarget.classList.add("btn-brand-neutral");
    }
  }

  updateFieldVisibility() {
    // Show/hide the autocomplete container based on mode
    if (this.hasAutocompleteContainerTarget) {
      if (this.manualModeValue) {
        this.autocompleteContainerTarget.classList.add("hidden");
      } else {
        this.autocompleteContainerTarget.classList.remove("hidden");
      }
    }

    // Show/hide the set_address container based on mode
    if (this.hasSetAddressContainerTarget) {
      if (this.manualModeValue) {
        this.setAddressContainerTarget.classList.remove("hidden");
      } else {
        this.setAddressContainerTarget.classList.add("hidden");
      }
    }
  }

  handleKeydown(event) {
    if (this.resultsTarget.classList.contains("hidden")) {
      return;
    }

    const items = this.resultsTarget.querySelectorAll(
      ".mapbox-autocomplete-item",
    );

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.currentFocus++;
        this.updateFocus(items);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.currentFocus--;
        this.updateFocus(items);
        break;
      case "Enter":
        event.preventDefault();
        if (this.currentFocus > -1 && items[this.currentFocus]) {
          const el = items[this.currentFocus];
          // Trigger Stimulus click handler if connected
          try {
            el.click();
          } catch (e) {}

          // Fallback: directly apply selection when click handler didn't run
          try {
            let suggestion = el.dataset.addressAutocompleteSuggestionParam;
            if (suggestion) suggestion = JSON.parse(suggestion);
            const addressValue =
              (suggestion && (suggestion.place_name || suggestion.address)) ||
              el.textContent.trim();
            if (addressValue) {
              this.inputTarget.value = addressValue;
              if (this.hasSetAddressFieldTarget) {
                this.setAddressFieldTarget.value = addressValue;
              }
              // Track the chosen value and guard subsequent change events
              this.lastSelectedValue = addressValue;
              this.justSelectedUntil = Date.now() + 800;
            }
          } catch (_) {}

          // Ensure dropdown closes either way
          this.hideResults();
          this.suppressNextSearch = true;
          this.inputTarget.dispatchEvent(
            new Event("change", { bubbles: true }),
          );
        }
        break;
      case "Escape":
        event.preventDefault();
        this.hideResults();
        break;
    }
  }

  updateFocus(items) {
    if (this.currentFocus >= items.length) this.currentFocus = 0;
    if (this.currentFocus < 0) this.currentFocus = items.length - 1;

    items.forEach((item, index) => {
      if (index === this.currentFocus) {
        item.classList.add("bg-blue-50");
        item.setAttribute("aria-selected", "true");
      } else {
        item.classList.remove("bg-blue-50");
        item.setAttribute("aria-selected", "false");
      }
    });
  }

  hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden");
      this.resultsTarget.style.display = "none";
      this.resultsTarget.innerHTML = "";
    }
    this.currentFocus = -1;
  }

  showLoading() {
    this.hideError();
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden");
      this.loadingTarget.style.display = "block";
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden");
      this.loadingTarget.style.display = "none";
    }
  }

  showError() {
    this.hideLoading();
    this.hideResults();
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("hidden");
      this.errorTarget.style.display = "block";
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
      this.errorTarget.style.display = "none";
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // Update set_address when manual field changes
  updateSetAddress(event) {
    if (this.hasSetAddressFieldTarget) {
      this.setAddressFieldTarget.value = event.target.value;
    }
  }

  // Called when clicking outside
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults();
    }
  }

  disableBrowserAutocomplete() {
    if (!this.inputTarget) return;

    // Set a random autocomplete value to prevent browser suggestions
    const randomStr = "search-" + Math.random().toString(36).substr(2, 9);
    this.inputTarget.setAttribute("autocomplete", randomStr);

    // Additional attributes to discourage autocomplete
    this.inputTarget.setAttribute("autocorrect", "off");
    this.inputTarget.setAttribute("autocapitalize", "off");
    this.inputTarget.setAttribute("spellcheck", "false");

    // Re-randomize on focus to keep browser confused
    if (!this.inputTarget.hasAttribute("data-autocomplete-listener")) {
      this.inputTarget.setAttribute("data-autocomplete-listener", "true");
      this.inputTarget.addEventListener("focus", () => {
        const newRandom = "search-" + Math.random().toString(36).substr(2, 9);
        this.inputTarget.setAttribute("autocomplete", newRandom);
      });
    }
  }
}
